import React from 'react';
import {
  TextField,
  InputAdornment,
} from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

export interface MuiTextInputProps extends Omit<TextFieldProps, 'onChange'> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  startIcon?: React.ReactNode;
  errorMessage?: string;
}

export const speedysseyInputSx = {
  width: '100%',
  '& .MuiInputBase-root': {
    fontSize: 14,
    backgroundColor: '#F9FAFB',
    borderRadius: '14px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    fontFamily: 'Satoshi, sans-serif',
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    color: '#2C2C2C',
    fontFamily: 'Satoshi, sans-serif',
    '&::placeholder': {
      color: '#9CA3AF',
      opacity: 1,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E5E7EB',
    borderRadius: '14px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D1D5DB',
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#F55109 !important',
    borderWidth: '1.5px',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Satoshi, sans-serif',
    fontSize: '14px',
    color: '#4B5563',
    '&.Mui-focused': {
      color: '#F55109',
    },
  },
};

export const MuiTextInput: React.FC<MuiTextInputProps> = ({
  label,
  value,
  onChange,
  startIcon,
  errorMessage,
  required,
  multiline,
  rows,
  placeholder,
  type = 'text',
  ...rest
}) => {
  return (
    <TextField
      fullWidth
      variant="outlined"
      label={label}
      value={value}
      type={type}
      required={required}
      multiline={multiline}
      rows={rows}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      error={!!errorMessage}
      helperText={errorMessage}
      slotProps={{
        input: {
          startAdornment: startIcon ? (
            <InputAdornment position="start" sx={{ color: '#9CA3AF' }}>
              {startIcon}
            </InputAdornment>
          ) : undefined,
        },
      }}
      sx={speedysseyInputSx}
      {...rest}
    />
  );
};

export interface MuiDatePickerFieldProps {
  label: string;
  value: string; // YYYY-MM-DD
  onChange: (dateStr: string) => void;
  errorMessage?: string;
  required?: boolean;
}

export const MuiDatePickerField: React.FC<MuiDatePickerFieldProps> = ({
  label,
  value,
  onChange,
  errorMessage,
  required,
}) => {
  const dayjsValue = value ? dayjs(value) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={dayjsValue}
        minDate={dayjs()}
        disablePast
        onChange={(newDate: Dayjs | null) => {
          if (newDate && newDate.isValid()) {
            onChange(newDate.format('YYYY-MM-DD'));
          } else {
            onChange('');
          }
        }}
        slotProps={{
          textField: {
            fullWidth: true,
            required,
            error: !!errorMessage,
            helperText: errorMessage,
            sx: speedysseyInputSx,
          },
        }}
      />
    </LocalizationProvider>
  );
};
